import { useEffect, useContext, useRef, useState, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { useReactFlow, getOutgoers } from "reactflow";
import { styled } from "@mui/material/styles";
import MuiDivider from "@mui/material/Divider";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import ValueInput from "./ValueInput";
import NewBranchButton from "./NewBranchButton";
import { SELECTION_OPERATORS_DATASOURCE } from "constants/flowDesigner";
import { StyledSelect } from "../StyledComponents";
import useGetBranchNodes from "hooks/useGetBranchNodes";
import { tryCatchWrapper } from "helpers/common";
import ErrorHandlingDropdownListItem from "../ErrorHandlingDropdownListItem";
import { ACTION_TYPES } from "constants/flowDesigner";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { TextInput } from "../TextInput";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { COMPONENT_BRANCH_FIELDS } from "constants/flowDesigner";

const ConditionalWrapper = styled(SoftBox)(() => ({
   marginBottom: "20px",
   marginTop: "-16px",
   overflow: "hidden",
   overflowY: "auto",
   padding: "0 8px 8px 4px"
}));

const StyledDivider = styled(MuiDivider)(({ theme }) => ({
   marginTop: theme.spacing(2.5),
   marginBottom: theme.spacing(1.5)
}));

const ERROR_HANDLING_ACTION = {
   field: "errorHandling",
   label: "Error Handling",
   type: ACTION_TYPES.ERROR_HANDLING_DROPDOWN,
   value: "stop",
   options: [
      {
         value: "continue",
         text: "Continue on error"
      },
      {
         value: "stop",
         text: "Stop on error"
      }
   ]
};

const SelectionComponentForm = ({ ref, tabId }) => {
   const tabState = useSelector((store) => store?.designer?.componentPropsPanel?.tabs?.[tabId] || {});
   const { flowElement, selectedNode } = tabState;
   const { actionForm } = useContext(ComponentPropsContext);
   const { setActionForm, setInitialActionForm } = useContext(ComponentPropsContextApi);
   const { getNode, getNodes, getEdges, setNodes, setEdges } = useReactFlow();
   const formRef = useRef(null);
   const { getMergeNodeId } = useGetBranchNodes();
   const options = SELECTION_OPERATORS_DATASOURCE.sortAlphabetical("text").map((entry) => {
      if (typeof entry === "object") {
         return {
            value: entry.value,
            label: entry.text,
            isDisabled: entry.disabled
         };
      }
      return {
         value: entry,
         label: entry
      };
   });
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const [removedBranchData, setRemoveBranchData] = useState({
      nodes: [],
      edges: [],
      branches: []
   });

   const parseComponentData = () => {
      const { data } = flowElement.flowElement || flowElement.data || {};
      const entity = flowElement?.elementId ? flowElement : selectedNode;
      const node = getNode(entity?.elementId);

      if (!node) return;

      const outgoers = getOutgoers(node, getNodes(), getEdges());
      const branches = [];

      outgoers.forEach((out, index) => {
         const storedBranch = getBranchDataById(out.id);
         const { id, key, value, startPoint, endPoint, isDefaultEnabled } = storedBranch ?? {};
         const branchName = out.data?.label;
         const newKey = key || `${entity.elementId}-${branchName?.replace(/ /g, "").toLowerCase()}`;

         branches.push({
            startPoint: startPoint || entity?.elementId,
            endPoint: endPoint || node.data.mergeId || getMergeNodeId(out),
            name: branchName,
            id: id || out.id,
            key: newKey,
            label: branchName,
            value: value || "",
            ...(index===0 && {
               isDefaultEnabled: storedBranch ? (isDefaultEnabled ?? Boolean(value)) : true
            })
         });
      });

      setTimeout(() => {
         const formValues = {
            inputValue: {
               value: data?.inputValue?.value || actionForm?.inputValue?.value || "",
               key: "inputValue",
               label: "Input Value",
               isVariable: !!data?.inputValue?.isVariable
            },
            operator: {
               value: data?.operator?.value || actionForm?.operator?.value || "eq" || ""
            },
            branches,
            errorHandling: {
               value: data?.errorHandling?.value || actionForm?.errorHandling?.value || "stop"
            },
            description: {
               value: data?.description?.value || actionForm?.description?.value || "",
               isVariable: false
            }
         };
         setActionForm(formValues);
         setInitialActionForm(JSON.parse(JSON.stringify(formValues)));
      }, 10);
   };

   const getBranchDataById = (id) => {
      const data = flowElement.flowElement?.data || flowElement.data;
      if (data) {
         return data.branches?.find((branch) => branch.id === id);
      }
      return undefined;
   };

   const handleOperatorChange = (e, item) => {
      const { name } = item;
      let copy = { ...actionForm };

      copy = { ...actionForm, [name]: { ...actionForm[name], value: e.value } };
      setActionForm(copy);
   };

   const getFieldValue = (action) => {
      const defaultValue = action.type === ACTION_TYPES.KEY_VALUE ? [] : "";
      return actionForm[action?.field]?.value || defaultValue;
   };

   useImperativeHandle(ref, () => {
      return {
         revertBranches() {
            setNodes((prev) => {
               const restored = [...prev];
               [...removedBranchData.nodes].reverse().forEach((branchNodes) => {
                  branchNodes.forEach(({ node, index }) => {
                     restored.splice(index, 0, node);
                  });
               });

               const updated = restored.map((node) =>
                  node.id === flowElement.elementId
                     ? {
                          ...node,
                          data: {
                             ...node.data,
                             flowElement: node.data?.flowElement?.data
                                ? {
                                     ...node.data.flowElement,
                                     data: {
                                        ...node.data.flowElement.data,
                                        branches: [...(node.data.flowElement.data.branches || []), ...(removedBranchData.branches || [])]
                                     }
                                  }
                                : {}
                          }
                       }
                     : node
               );

               return updated;
            });

            setEdges((prev) => {
               const restored = [...prev];
               [...removedBranchData.edges].reverse().forEach((branchEdges) => {
                  branchEdges.forEach(({ edge, index }) => {
                     restored.splice(index, 0, edge);
                  });
               });
               return restored;
            });

            setActionForm((prev) => ({
               ...prev,
               branches: [...prev.branches, ...removedBranchData.branches]
            }));

            setRemoveBranchData({ nodes: [], edges: [], branches: [] });
         },
         resetToDefault() {
            setRemoveBranchData({ nodes: [], edges: [], branches: [] });
         }
      };
   }, [removedBranchData, setNodes, setEdges]);

   useEffect(() => {
      if (flowElement) {
         tryCatchWrapper(parseComponentData)();
      }
   }, [flowElement]);

   return (
      <ConditionalWrapper ref={formRef}>
         <TextInput
            action={{
               field: "description",
               label: "Description",
               multiline: true,
               type: ACTION_TYPES.TEXT,
               disabled: isFlowActive || !allowEditWorkflow(isReadOnlyAccess)
            }}
            getFieldValue={getFieldValue}
         />
         <ValueInput field={actionForm?.inputValue} name="inputValue" allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         <StyledSelect
            value={options.find((o) => o.value === actionForm.operator?.value)}
            name="operator"
            isSearchable={false}
            placeholder="Operator"
            label="Operator"
            options={options}
            onChange={handleOperatorChange}
            isDisabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}
         />
         <StyledDivider />
         <SoftBox>
            <SoftTypography variant="subtitle2">Branches</SoftTypography>
         </SoftBox>
         <List>
            {actionForm?.branches?.map((branch, index) => (
               <ListItem key={branch.key + index}>
                  <ValueInput field={branch} name={COMPONENT_BRANCH_FIELDS.VALUE} isBranch allowEditWorkflow={allowEditWorkflow} setRemoveBranchData={setRemoveBranchData} index={index} />
               </ListItem>
            ))}
         </List>

         <NewBranchButton tabId={tabId} formRef={formRef} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         <StyledDivider />
         <ErrorHandlingDropdownListItem action={ERROR_HANDLING_ACTION} fieldValue={actionForm?.errorHandling?.value || "stop"} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
      </ConditionalWrapper>
   );
};

export default SelectionComponentForm;
